import type { DisplayedSubjectQuote, QuoteStatus } from '../../../types';
import { normalizeQuoteStatus } from '../../../types';

export type QuoteGroup = {
  status: QuoteStatus;
  title: string;
  quotes: DisplayedSubjectQuote[];
  /** Sum of the quotes' amounts, `null` when none of them has an amount. */
  totalAmount: number | null;
};

const GROUP_TITLES: Record<QuoteStatus, string> = {
  pending: 'En attente',
  accepted: 'Validés',
  refused: 'Refusés',
};

/** Decisions first: pending, then accepted, then refused. */
const GROUP_ORDER: QuoteStatus[] = ['pending', 'accepted', 'refused'];

function getTime(date: string | null | undefined) {
  return date ? new Date(date).getTime() : 0;
}

function sumAmounts(quotes: DisplayedSubjectQuote[]) {
  const amounts = quotes
    .map((quote) => (quote.amount == null ? null : Number(quote.amount)))
    .filter((amount): amount is number => amount !== null && !Number.isNaN(amount));

  return amounts.length > 0 ? amounts.reduce((sum, amount) => sum + amount, 0) : null;
}

/**
 * Splits quotes into non-empty status groups. Within a group, the most recent quote comes first:
 * by approval date for accepted quotes, by reception date otherwise.
 */
export function groupQuotesByStatus(quotes: DisplayedSubjectQuote[]): QuoteGroup[] {
  const byStatus: Record<QuoteStatus, DisplayedSubjectQuote[]> = {
    pending: [],
    accepted: [],
    refused: [],
  };

  for (const quote of quotes) {
    byStatus[normalizeQuoteStatus(quote.status)].push(quote);
  }

  byStatus.pending.sort((a, b) => getTime(b.date_received) - getTime(a.date_received));
  byStatus.refused.sort((a, b) => getTime(b.date_received) - getTime(a.date_received));
  byStatus.accepted.sort(
    (a, b) =>
      getTime(b.date_approved ?? b.date_received) - getTime(a.date_approved ?? a.date_received)
  );

  return GROUP_ORDER.filter((status) => byStatus[status].length > 0).map((status) => ({
    status,
    title: GROUP_TITLES[status],
    quotes: byStatus[status],
    totalAmount: status === 'refused' ? null : sumAmounts(byStatus[status]),
  }));
}
